import { Component, Input } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { McpConnector, McpNotice, McpService, McpTool } from '../../../services/mcp.service';

/**
 * The responsibility step (PRD agent-mcp-connectors, "Verantwortung").
 * Not a checkbox inside a form: its own dialog with the three points in
 * plain words — third-party fees, content, and tool descriptions acting as
 * instructions — and the exact tools it covers. The backend records who,
 * when, which server and which tools in the approved wording.
 *
 * The confirm button stays clickable (CLAUDE.md rule 16); unticked points are
 * marked instead.
 */
@Component({
  selector: 'app-mcp-acknowledge-dialog',
  templateUrl: './mcp-acknowledge-dialog.component.html',
  styleUrl: './mcp-acknowledge-dialog.component.scss',
})
export class McpAcknowledgeDialogComponent {
  @Input() connector!: McpConnector;
  @Input() onDone: (connector: McpConnector) => void = () => {};

  readonly notices: McpNotice[] = ['fees', 'content', 'instructions'];
  checked: Record<McpNotice, boolean> = { fees: false, content: false, instructions: false };
  attempted = false;
  saving = false;
  error: string | null = null;

  constructor(
    protected dialogRef: NbDialogRef<McpAcknowledgeDialogComponent>,
    private mcp: McpService,
    private t: TranslateService
  ) {}

  get enabledTools(): McpTool[] {
    return this.connector.tools.filter((tool) => tool.is_enabled && !tool.removed);
  }

  get allChecked(): boolean {
    return this.notices.every((n) => this.checked[n]);
  }

  get availableCount(): number {
    return this.connector.tools.filter((tool) => !tool.removed).length;
  }

  /** Nothing released yet: release every tool right here. */
  enableAll(): void {
    this.saving = true;
    this.error = null;
    this.mcp.setAllTools(this.connector.id, true).subscribe({
      next: (saved) => {
        this.saving = false;
        this.connector = saved;
      },
      error: (err) => {
        this.saving = false;
        const detail = err?.error?.detail;
        this.error = typeof detail === 'string' ? detail : this.t.instant('pages.mcp.errors.saveFailed');
      },
    });
  }

  confirm(): void {
    this.attempted = true;
    this.error = null;
    if (!this.enabledTools.length) {
      // never a silent click: say what is missing
      this.error = this.t.instant('pages.mcp.ack.needTools');
      return;
    }
    if (!this.allChecked) return;
    this.saving = true;
    this.mcp.acknowledge(this.connector.id, this.notices).subscribe({
      next: (saved) => {
        this.saving = false;
        this.onDone(saved);
        this.dialogRef.close();
      },
      error: (err) => {
        this.saving = false;
        const detail = err?.error?.detail;
        this.error = typeof detail === 'string' ? detail : this.t.instant('pages.mcp.errors.saveFailed');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
